import React, { useEffect, useMemo, useRef } from 'react';
import { useObserver } from 'mobx-react-lite';

import { Product } from 'types/graphql-overrides';
import { ProductCardTextAlignmentOption } from 'src/gql/graphql';
import useUI from 'src/hooks/use-ui';
import useViewportVisibility from 'src/hooks/use-viewport-visibility';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import { hasSelectableOptions, imageToUse, isWeightedProduct } from 'shared/helpers/products';
import { getComplianceConfig } from 'shared/core/helpers/compliance';
import { useListPriceTreatment } from 'src/hooks/use-list-price-treatment';
import { useCard } from './use-card';
import {
  ProductCardProduct,
  ProductCardImage,
  ProductCardBadges,
  ProductCardPricing,
  ProductCardOptions,
  ProductCardLayout,
  ListPriceConfig,
  CollectionBadgeViewData,
  FullCardOptionViewData,
} from './types';

type UseProductCardDataProps = {
  product: Product;
  productIndex: number;
  onView?: (product: Product, index: number) => void;
  viewportThreshold?: number;
};

type UseProductCardDataReturn = {
  product: ProductCardProduct;
  image: ProductCardImage;
  badges: ProductCardBadges;
  pricing: ProductCardPricing;
  options: ProductCardOptions;
  listPrice: ListPriceConfig;
  layout: ProductCardLayout;
  containerRef: React.Ref<HTMLDivElement>;
  productIndex: number;
};

/**
 * Consolidated data hook for product card components.
 * Calls all internal hooks and returns pre-computed, grouped data suitable for pure view components.
 */
export const useProductCardData = ({
  product,
  productIndex,
  onView,
  viewportThreshold,
}: UseProductCardDataProps): UseProductCardDataReturn => {
  const {
    href,
    isStaffPick,
    isSponsored,
    brandName,
    strainType,
    potency,
    isSpecialOffer,
    specialNames,
    specials,
    hideDiscount,
    hideSalesLanguage,
    priceOptions,
    collectionCardBadge,
  } = useCard({ product });

  const UI = useUI();
  const { isEmbeddedCarousel } = UI;

  // Threshold 0.5 ensures cards mostly hidden by a carousel's overflow
  // don't fire impressions. Using 0.5 instead of 1.0 to avoid
  // floating-point precision issues in IntersectionObserver ratio calculations.
  const { ref: containerRef, hasBeenVisible } = useViewportVisibility(
    viewportThreshold !== undefined ? { threshold: viewportThreshold } : undefined
  );

  const { listPriceEnabled, hideStrikethrough, formatStandardPrice } = useListPriceTreatment();
  const { dispensary } = useDispensary();

  const { fullWidthImages = false, showProductWeight = false, textAlignment = ProductCardTextAlignmentOption.left } =
    dispensary?.webCustomizationSettingsV2?.productCards ?? {};

  // Pre-compute image watermark config from state compliance rules
  const useWatermarks = dispensary?.storeSettings?.useStateRegulatedImageWatermarks;
  const state = dispensary?.location?.state;
  const imgixAttrs = useMemo(() => (useWatermarks ? getComplianceConfig(state, 'imageWatermarkConfig') ?? {} : {}), [
    useWatermarks,
    state,
  ]);

  // Pre-compute collection badge visibility (absorbs auth check from CollectionBadge component)
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const collectionBadge = useMemo((): CollectionBadgeViewData | null => {
    if (!collectionCardBadge) {
      return null;
    }
    const { color, isAccessibleToCurrentUser, isExclusive, title } = collectionCardBadge;
    if (isExclusive && (!isLoggedIn || !isAccessibleToCurrentUser)) {
      return null;
    }
    return {
      color: color ?? null,
      isExclusive: !!isExclusive,
      title: title ?? '',
    };
  }, [collectionCardBadge, isLoggedIn]);

  // Fire onView callback when card becomes visible
  const onViewRef = useRef(onView);
  useEffect(() => {
    if (hasBeenVisible) {
      onViewRef.current?.(product, productIndex);
    }
  }, [product, productIndex, hasBeenVisible]);

  // Pre-compute pricing for Card view (first option)
  const firstOption = priceOptions[0] ?? { formattedPrice: '', formattedDiscount: '', special: null, label: '' };
  const { formattedPrice, formattedDiscount, special, label: weightLabel } = firstOption;
  const displayPrice = special ? special.formattedPrice : formattedPrice;
  const standardPrice = special ? formattedPrice : null;
  const discount = special && !hideDiscount && !hideSalesLanguage ? formattedDiscount : null;

  const isWeighted = isWeightedProduct(product);
  const showWeight = isWeighted && showProductWeight && !!weightLabel && weightLabel !== 'N/A';
  const numberOfOptions = product.Options?.length ?? 0;
  // PF-8648 (work-cluster 3392c08a-1467-4710-a38f-d4332e0111cd): preserve the original
  // weighted-product rule (needs >1 options) exactly, and additionally cover apparel products
  // with multiple size options via hasSelectableOptions (which already requires >1 usable
  // options for non-weighted categories) so the button says "Select size" instead of the
  // misleading "Add to cart".
  const needsOptionSelection = isWeighted ? numberOfOptions > 1 : hasSelectableOptions(product);
  let buttonCopy = 'Add to cart';
  if (needsOptionSelection) {
    buttonCopy = isWeighted ? 'Select weight' : 'Select size';
  }

  // Pre-compute option data for FullCard view (all options with pricing resolved)
  const items: FullCardOptionViewData[] = useMemo(
    () =>
      priceOptions.map(
        ({ value, label, special: optSpecial, formattedPrice: optPrice, formattedDiscount: optDiscount }) => ({
          value,
          label,
          currentPrice: optSpecial ? optSpecial.formattedPrice : optPrice,
          originalPrice: optSpecial ? optPrice : null,
          discountLabel: optSpecial && !hideDiscount && !hideSalesLanguage ? optDiscount : null,
        })
      ),
    [priceOptions, hideDiscount, hideSalesLanguage]
  );

  return {
    product: {
      id: product.id ?? '',
      name: product.name,
      brandName,
      href,
      strainType: strainType ?? null,
      potency,
      avgRating: product.avgRating ?? null,
      reviewCount: product.reviewCount ?? null,
    },
    image: {
      images: product.images ?? [],
      fallbackImage: imageToUse(product),
      imgixAttrs,
      disableCycling: dispensary?.webCustomizationSettingsV2?.productCards?.disableProductCardImageCycling ?? false,
    },
    badges: {
      isStaffPick,
      isSponsored,
      isSpecialOffer,
      isComingSoon: !!product.comingSoon,
      specialNames,
      specials,
      hideSalesLanguage,
      collectionBadge,
    },
    pricing: {
      displayPrice,
      standardPrice,
      discount,
      showWeight,
      weight: weightLabel,
      buttonCopy,
      listPriceEnabled,
      hideStrikethrough,
      formatStandardPrice,
    },
    options: {
      items,
      totalCount: priceOptions.length,
      hasMultiple: priceOptions.length > 1,
      shouldDisplayLabel: isWeighted || priceOptions.length > 1,
    },
    listPrice: {
      listPriceEnabled,
      hideStrikethrough,
      formatStandardPrice,
    },
    layout: {
      isEmbeddedCarousel,
      fullWidthImages,
      textAlignment,
    },
    containerRef,
    productIndex,
  };
};
